import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { getSubscriptionMe } from '../api/subscriptions.js';
import { UpgradeCta } from './UpgradeCta.js';

/**
 * Layout-level subscription banner (Phase C-3). Driven by GET
 * /v1/subscriptions/me — NOT by intercepting the middleware's 403 (the tenant
 * client has no global 403 handler). Mounted in BOTH tenant layouts:
 *   - admin  (AuthedLayout):  trial countdown + read-only/suspended/cancelled.
 *   - employee (EmployeeLayout): read-only/suspended/cancelled only, with
 *     employee-facing copy ("your employer's account…"); no trial countdown.
 *
 * Renders nothing while loading, on error (e.g. orphan 404), or for a writable
 * non-trial subscription.
 */
export function SubscriptionBanner({ mode }: { mode: 'admin' | 'employee' }): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ['subscription', 'me'],
    queryFn: getSubscriptionMe,
    retry: 1,
    staleTime: 60_000, // layout-level — don't refetch on every navigation
  });

  if (!data) return null;

  // ADR-0032 D7 — server-derived; never re-derived from tier/paymentProvider.
  const invoiced = data.billingChannel === 'invoiced';

  const blocked =
    data.effectiveStatus === 'read_only' ||
    data.effectiveStatus === 'suspended' ||
    data.effectiveStatus === 'cancelled';

  // --- Trial countdown (admin only, not blocked) ---------------------------
  if (mode === 'admin' && data.status === 'trial' && !blocked && data.daysRemainingInTrial !== null) {
    const days = data.daysRemainingInTrial;
    const urgent = days <= 3;
    const countdown =
      days === 0 ? 'Your trial ends today.' : days === 1 ? 'Your trial ends tomorrow.' : `${days} days left in your trial.`;
    return (
      <>
        <div
          role="alert"
          className={`w-full px-4 py-2 text-sm border-b ${
            urgent ? 'bg-orange-100 text-orange-900 border-orange-300' : 'bg-blue-100 text-blue-900 border-blue-300'
          }`}
        >
          <div className="flex items-center justify-between gap-4">
            <span>{countdown} Upgrade to keep full access.</span>
            <UpgradeCta tier={data.tier} label="Upgrade" />
          </div>
        </div>
      </>
    );
  }

  // --- Past due (admin only; writes still allowed, grace countdown) --------
  // past_due is NOT a blocked state — the employer can still work, so this is
  // an admin-facing warning (not shown to employees, mirroring the trial
  // countdown). An INVOICED tenant (ADR-0032 D8) is told which invoice and
  // linked to /billing, which lists it with how to pay. A self-serve tenant
  // keeps the /billing checkout CTA.
  if (mode === 'admin' && data.status === 'past_due' && !blocked) {
    const days = data.graceDaysRemaining;
    const daysCopy = days == null ? 'A few days' : days <= 0 ? 'Less than a day' : days === 1 ? '1 day' : `${days} days`;
    const lead = !invoiced
      ? 'Payment overdue.'
      : data.openInvoice
        ? `Invoice ${data.openInvoice.number} is ${data.openInvoice.status === 'overdue' ? 'overdue' : 'awaiting payment'}.`
        : 'Your subscription period has ended. Contact billing to renew.';
    return (
      <>
        <div role="alert" className="w-full px-4 py-2 text-sm border-b bg-orange-100 text-orange-900 border-orange-300">
          <div className="flex items-center justify-between gap-4">
            <span>
              {lead} {daysCopy} remaining before write access is restricted.
            </span>
            {invoiced ? (
              <InvoicedBillingLink label="View invoices" />
            ) : (
              <UpgradeCta tier={data.tier} label="Manage billing" intent="billing" />
            )}
          </div>
        </div>
      </>
    );
  }

  // --- Read-only / suspended / cancelled (both modes) ----------------------
  if (blocked) {
    const adminCopy =
      data.effectiveStatus === 'read_only'
        ? 'Access restricted. Sign in and view your data; adding employees, running payroll, and submitting leave are paused until payment completes.'
        : data.effectiveStatus === 'suspended'
          ? 'Your account is suspended. Please contact support.'
          : invoiced
            ? 'Your subscription has been cancelled. Contact billing to restore access.'
            : 'Your subscription has been cancelled. Resubscribe to restore access.';
    const employeeCopy =
      data.effectiveStatus === 'read_only'
        ? "Your employer's account is in read-only mode. Please contact your HR administrator."
        : data.effectiveStatus === 'suspended'
          ? "Your employer's account is suspended. Please contact your HR administrator."
          : "Your employer's account has been cancelled. Please contact your HR administrator.";
    // read_only → "Renew"; cancelled → "Resubscribe" (both self-serve via /billing,
    // where cancelled now renders a paid reactivation). `suspended` is an operator
    // freeze — no self-serve CTA, contact support only.
    const ctaLabel =
      data.effectiveStatus === 'read_only'
        ? 'Renew to restore access'
        : data.effectiveStatus === 'cancelled'
          ? 'Resubscribe'
          : null;
    return (
      <>
        <div role="alert" className="w-full px-4 py-2 text-sm border-b bg-red-100 text-red-900 border-red-300">
          <div className="flex items-center justify-between gap-4">
            <span>{mode === 'admin' ? adminCopy : employeeCopy}</span>
            {mode === 'admin' && ctaLabel ? (
              invoiced ? (
                // An invoiced tenant cannot self-serve a renewal or a resubscribe;
                // /billing shows what is owed and how to pay (ADR-0032 D8).
                <InvoicedBillingLink label="View invoices" />
              ) : (
                <UpgradeCta tier={data.tier} label={ctaLabel} intent="billing" />
              )
            ) : null}
          </div>
        </div>
      </>
    );
  }

  return null;
}

function InvoicedBillingLink({ label }: { label: string }): JSX.Element {
  return (
    <Link to="/billing" className="shrink-0 font-medium underline hover:no-underline">
      {label}
    </Link>
  );
}
